import React, { useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Apexcharts from 'react-apexcharts';
import CountUp from 'react-countup';
import { CircularProgress } from '@material-ui/core';
import constants from '../../../constants';

const ExpandedChartView = ({ isOpen, onClose, widget, data, isLoading }) => {
  const history = useHistory();
  const { workspaceId } = useParams();

  // Generate chart options for expanded view
  const chartConfig = useMemo(() => {
    if (!data || data.error) return null;

    // Helper function to extract categories
    const extractCategories = (data) => {
      if (data.categories?.length > 0) return data.categories;
      if (data.labels?.length > 0) return data.labels;
      if (data.timeSeries?.length > 0) return data.timeSeries.map(d => d.date);
      if (data.distribution?.length > 0) return data.distribution.map(d => d.label);
      return [];
    };

    // Helper function to extract series data
    const extractSeriesData = (data, title) => {
      if (data.series?.length > 0 && Array.isArray(data.series[0]?.data)) return data.series;
      if (data.timeSeries?.length > 0) return [{ name: title, data: data.timeSeries.map(d => d.value || 0) }];
      if (data.distribution?.length > 0) return [{ name: title, data: data.distribution.map(d => d.value || 0) }];
      if (data.series?.length > 0 && typeof data.series[0] === 'number') return [{ name: title, data: data.series }];
      return [{ name: title, data: [] }];
    };

    // Helper function to extract donut series
    const extractDonutSeries = (data) => {
      if (data.series?.length > 0 && typeof data.series[0] === 'number') return data.series;
      if (data.distribution?.length > 0) return data.distribution.map(d => d.value || 0);
      if (data.timeSeries?.length > 0) return data.timeSeries.map(d => d.value || 0);
      if (data.series?.length > 0 && Array.isArray(data.series[0]?.data)) return data.series[0].data;
      return []
    };

    // Helper function to extract numeric value
    const extractNumericValue = (data) => {
      if (data.value !== undefined) return data.value;
      if (data.total !== undefined) return data.total;
      if (data.timeSeries?.length > 0) {
        return data.timeSeries.reduce((sum, d) => sum + (d.value || 0), 0);
      }
      if (data.series?.length > 0) {
        if (Array.isArray(data.series[0]?.data)) return data.series.reduce((sum, s) => sum + s.data.reduce((a, b) => a + b, 0), 0);
        return data.series.reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
      }
      return 0;
    };

    switch (widget?.type) {
      case 'number':
        const numValue = extractNumericValue(data);
        return {
          type: 'number',
          value: numValue,
          formatted: data.formatted || numValue?.toString() || '0'
        };

      case 'donut':
        const donutLabels = extractCategories(data);
        const donutSeries = extractDonutSeries(data);
        const donutTotal = donutSeries.reduce((sum, val) => sum + val, 0);
        return {
          type: 'donut',
          options: {
            chart: { type: 'donut' },
            labels: donutLabels,
            colors: constants.chartColors.slice(0, donutLabels.length || 6),
            legend: {
              position: 'right',
              fontSize: '14px',
              formatter: (seriesName, opts) => {
                const value = opts.w.globals.series[opts.seriesIndex];
                const total = opts.w.globals.series.reduce((a, b) => a + b, 0);
                const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${seriesName} ${value} (${percent}%)`;
              }
            },
            dataLabels: {
              enabled: false
            },
            plotOptions: {
              pie: {
                donut: {
                  size: '60%',
                  labels: {
                    show: true,
                    total: {
                      show: true,
                      label: 'Total',
                      fontSize: '16px',
                      fontWeight: 600,
                      formatter: () => donutTotal
                    }
                  }
                }
              }
            }
          },
          series: donutSeries
        };

      case 'column':
      case 'bar':
        const isHorizontal = widget.type === 'bar';
        const barCategories = extractCategories(data);
        const barSeries = extractSeriesData(data, widget.title);
        return {
          type: 'bar',
          options: {
            chart: { type: 'bar', toolbar: { show: true } },
            plotOptions: {
              bar: {
                horizontal: isHorizontal,
                borderRadius: 4,
                columnWidth: '50%',
                distributed: true
              }
            },
            colors: constants.chartColors,
            xaxis: {
              categories: barCategories,
              labels: { style: { fontSize: '12px' } }
            },
            yaxis: {
              labels: { style: { fontSize: '12px' } }
            },
            dataLabels: { enabled: false },
            legend: { show: false },
            grid: { borderColor: '#e7e7e7', strokeDashArray: 4 }
          },
          series: barSeries
        };

      case 'line':
        const lineCategories = extractCategories(data);
        const lineSeries = extractSeriesData(data, widget.title);
        const expandedLineColors = constants.chartColors.slice(0, lineSeries.length || 1);
        return {
          type: 'area',
          options: {
            chart: {
              type: 'area',
              toolbar: { show: true },
              zoom: { enabled: true }
            },
            colors: expandedLineColors,
            fill: {
              type: 'gradient',
              gradient: {
                shade: 'light',
                type: 'vertical',
                shadeIntensity: 0.3,
                gradientToColors: expandedLineColors.map(() => '#ffffff'),
                inverseColors: false,
                opacityFrom: 0.5,
                opacityTo: 0.1,
                stops: [0, 100]
              }
            },
            stroke: { curve: 'smooth', width: 2.5, lineCap: 'round' },
            xaxis: {
              categories: lineCategories,
              labels: { style: { fontSize: '12px', colors: '#94a3b8', fontWeight: 400 } },
              axisBorder: { show: false },
              axisTicks: { show: false }
            },
            yaxis: {
              labels: {
                style: { fontSize: '12px', colors: '#94a3b8', fontWeight: 400 },
                formatter: (val) => Math.round(val)
              },
              min: 0,
              tickAmount: 5
            },
            dataLabels: { enabled: false },
            legend: {
              show: true,
              position: 'bottom',
              horizontalAlign: 'center',
              fontSize: '13px',
              fontWeight: 500,
              markers: { width: 24, height: 4, radius: 2, offsetX: -4 },
              itemMargin: { horizontal: 20, vertical: 8 }
            },
            grid: {
              borderColor: '#f1f5f9',
              xaxis: { lines: { show: false } },
              yaxis: { lines: { show: true } }
            },
            markers: {
              size: 5,
              colors: ['#ffffff'],
              strokeColors: expandedLineColors,
              strokeWidth: 2.5,
              strokeOpacity: 1,
              fillOpacity: 1,
              shape: 'circle',
              hover: { size: 7, sizeOffset: 3 }
            },
            tooltip: { enabled: true, shared: true, intersect: false }
          },
          series: lineSeries
        };

      case 'table':
        // Extract table data from various data formats
        const tableRows = [];
        if (data.timeSeries?.length > 0) {
          data.timeSeries.forEach(item => {
            tableRows.push({ label: item.date, value: item.value || 0 });
          });
        } else if (data.distribution?.length > 0) {
          data.distribution.forEach(item => {
            tableRows.push({ label: item.label, value: item.value || 0 });
          });
        } else if (data.categories?.length > 0 && data.series?.length > 0) {
          const seriesData = Array.isArray(data.series[0]?.data) ? data.series[0].data : data.series;
          data.categories.forEach((cat, idx) => {
            tableRows.push({ label: cat, value: seriesData[idx] || 0 });
          });
        } else if (data.labels?.length > 0 && data.series?.length > 0) {
          data.labels.forEach((label, idx) => {
            tableRows.push({ label, value: data.series[idx] || 0 });
          });
        }
        const tableTotal = tableRows.reduce((sum, row) => sum + (row.value || 0), 0);
        const maxValue = Math.max(...tableRows.map(r => r.value || 0), 1);
        // Add percentage and bar width to each row
        tableRows.forEach((row, idx) => {
          row.percent = tableTotal > 0 ? ((row.value / tableTotal) * 100).toFixed(1) : 0;
          row.barWidth = maxValue > 0 ? ((row.value / maxValue) * 100) : 0;
          row.rank = idx + 1;
        });
        return {
          type: 'table',
          rows: tableRows,
          total: tableTotal
        };

      default:
        return null;
    }
  }, [widget?.type, widget?.title, data]);

  // Map metric names to database field names and labels to raw values
  const metricToDbField = {'call_successful': 'successEvalution', 'user_sentiment': 'sentiment', 'disconnection_reason': 'endReason', 'phone_inbound_outbound': 'callType' };

  // For these metrics, the label is already the raw DB value (case-sensitive)
  const metricsWithLabelAsValue = ['disconnection_reason'];
  const labelToRawValue = {  'successful': 'pass', 'unsuccessful': 'fail', 'positive': 'positive', 'neutral': 'neutral', 'negative': 'negative', 'inbound calls': 'inbound_call', 'outbound calls': 'outbound_call', 'voice calls': 'voice_call', 'cold calling': 'cold_calling' };

  // Generate data table
  const tableData = useMemo(() => {
    if (!data) return [];

    const isAgentMetric = widget?.metricCategory !== 'crm';
    const actionText = isAgentMetric ? 'Call History' : 'View Details';

    if (data.timeSeries) {
      return data.timeSeries.map(item => ({
        time: item.date,
        metrics: widget?.title || 'Value',
        value: item.value,
        action: actionText,
        filterType: 'date',
        filterValue: item.date
      }));
    }

    if (data.distribution) {
      const dbField = metricToDbField[widget?.metrics] || widget?.metrics;
      const useLabelAsValue = metricsWithLabelAsValue.includes(widget?.metrics);
      return data.distribution.map(item => ({
        time: '-',
        metrics: item.label,
        value: item.value,
        action: actionText,
        filterType: 'field',
        filterField: dbField,
        filterValue: useLabelAsValue ? item.label : (labelToRawValue[item.label?.toLowerCase()] || item.label?.toLowerCase())
      }));
    }

    return [];
  }, [data, widget?.title, widget?.metricCategory, widget?.metrics]);

  const renderChart = () => {
    if (isLoading) {
      return (
        <div className="expanded-loading">
          <CircularProgress size={40} />
        </div>
      );
    }

    if (!data || data.error) {
      return (
        <div className="expanded-error">
          <span>No data available</span>
        </div>
      );
    }

    if (!chartConfig) {
      return (
        <div className="expanded-error">
          <span>Invalid chart type</span>
        </div>
      );
    }

    if (chartConfig.type === 'number') {
      return (
        <div className="expanded-number-content">
          <div className="expanded-number-value">
            <CountUp end={chartConfig.value} duration={1} separator="," preserveValue />
          </div>
          {chartConfig.formatted !== chartConfig.value?.toString() && (
            <div className="expanded-number-formatted">{chartConfig.formatted}</div>
          )}
        </div>
      );
    }

    if (chartConfig.type === 'table') {
      return (
        <div className="expanded-table-content">
          <table className="expanded-data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Value</th>
                <th>Percentage (%)</th>
                <th style={{ width: '200px' }}>Distribution</th>
              </tr>
            </thead>
            <tbody>
              {chartConfig.rows.map((row, idx) => (
                <tr key={idx}>
                  <td className="table-rank">{row.rank}</td>
                  <td className="table-label">{row.label}</td>
                  <td className="table-value">{typeof row.value === 'number' ? row.value.toLocaleString() : row.value}</td>
                  <td className="table-percent">{row.percent}%</td>
                  <td className="table-bar-cell">
                    <div className="expanded-bar-container">
                      <div
                        className="expanded-bar"
                        style={{
                          width: `${row.barWidth}%`,
                          backgroundColor: constants.chartColors[idx % constants.chartColors.length]
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <Apexcharts options={chartConfig.options} series={chartConfig.series} type={chartConfig.type} height={350} />
    );
  };

  const getAgentLabel = () => {
    // For CRM widgets, show the object type
    if (widget?.metricCategory === 'crm') {
      const objectTypeLabels = {
        deals: 'Deals',
        contacts: 'Contacts',
        companies: 'Companies',
        activities: 'Activities'
      };
      return objectTypeLabels[widget?.crmObjectType] || 'CRM';
    }
    // For agent widgets
    if (!widget?.agentId || widget?.agentId === 'all') return 'All agents';
    return 'Selected agent';
  };

  // Handle action button click - navigate to appropriate page
  const handleActionClick = (row) => {
    const isAgentMetric = widget?.metricCategory !== 'crm';
    const basePath = workspaceId ? `/${workspaceId}` : '';

    if (isAgentMetric) {
      const params = new URLSearchParams();
      params.set('tab', 'callLogs');

      if (widget?.agentId && widget.agentId !== 'all') params.set('agentId', widget.agentId);
      

      // Pass filter - use pre-mapped database field and raw value
      if (row.filterType === 'date' && row.filterValue) {
        params.set('date', row.filterValue);
      } else if (row.filterType === 'field' && row.filterField && row.filterValue) {
        params.set('filterField', row.filterField);
        params.set('filterValue', row.filterValue);
      }
      history.push(`${basePath}/phones?${params.toString()}`);
    } else {
      const objectType = widget?.crmObjectType || 'deals';
      history.push(`${basePath}/objects/${objectType}`);
    }
  };

  if (!isOpen || !widget) return null;

  return (
    <div className="expanded-page-overlay">
      <div className="expanded-page">
        {/* Header with breadcrumb */}
        <div className="expanded-page-header">
          <div className="expanded-breadcrumb">
            <button className="breadcrumb-link" onClick={onClose}>
              Dashboard
            </button>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span className="breadcrumb-current">{widget.title}</span>
          </div>
          <button className="expanded-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="expanded-page-content">
          {/* Chart Card */}
          <div className="expanded-chart-card">
            <div className="expanded-chart-card-header">
              <div>
                <h2 className="expanded-chart-title">{widget.title}</h2>
                <span className="expanded-chart-subtitle">{getAgentLabel()}</span>
              </div>
            </div>
            <div className="expanded-chart-area">
              {renderChart()}
            </div>
          </div>

          {/* Data Table */}
          {tableData.length > 0 && (
            <div className="expanded-table-card">
              <table className="expanded-data-table">
                <thead>
                  <tr>
                    <th>TIME</th>
                    <th>METRICS</th>
                    <th>VALUE</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                      <td className="table-time">{row.time}</td>
                      <td className="table-metrics">{row.metrics}</td>
                      <td className="table-value">{typeof row.value === 'number' ? row.value.toLocaleString() : row.value}</td>
                      <td>
                        <button className="table-action-link" onClick={() => handleActionClick(row)}>{row.action}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandedChartView;
